import type { ReactElement } from 'react';
import { Box, Stack, Text } from '@mantine/core';
import DetailDrawer from '@/design/DetailDrawer';
import StatTile from '@/design/StatTile';
import KeyValueTable from '@/design/KeyValueTable';
import PolicyOutputChip from './PolicyOutputChip';
import ConversationViewer from '@/shared/surfaces/ConversationViewer';
import { formatPct, formatWhen } from './PolicyFormatting';
import type { ClassifiedItem } from './PolicyVerdict';

/** A classified turn paired with the policy it was flagged under. */
export interface SelectedTurn {
    item: ClassifiedItem;
    policyName: string;
}

function sectionLabel(text: string): ReactElement {
    return (
        <Text size="xs" fw={700} tt="uppercase" mb="sm" style={{ fontSize: '9px', letterSpacing: '0.08em', color: 'var(--bm-color-text-dimmed)' }}>
            {text}
        </Text>
    );
}

/** Right-side drawer showing a classified turn's conversation transcript and its classification verdict. */
export default function PolicyTurnDrawer({ selected, onClose }: { selected: SelectedTurn | null; onClose: () => void }): ReactElement {
    const item = selected?.item ?? null;
    return (
        <DetailDrawer
            opened={selected !== null}
            onClose={onClose}
            size={680}
            avatarSource={item?.person ?? ''}
            title={selected === null ? '' : selected.policyName}
            subtitle={item === null ? '' : `${item.person} · ${formatWhen(item.timestampIso)}`}
            stats={
                selected === null ? undefined : (
                    <>
                        <StatTile label="Verdict">
                            <PolicyOutputChip output={selected.item.output} tone={selected.item.tone} />
                        </StatTile>
                        <StatTile label="Confidence">
                            <Text size="xs" fw={700} ff="monospace">
                                {formatPct(selected.item.confidence)}
                            </Text>
                        </StatTile>
                        <StatTile label="Department">
                            <Text size="xs" fw={700}>
                                {selected.item.department}
                            </Text>
                        </StatTile>
                    </>
                )
            }
        >
            {selected !== null && (
                <Stack gap="xl">
                    <Box>
                        {sectionLabel('Conversation')}
                        <ConversationViewer sessionId={selected.item.sessionId} focusTurnIndex={selected.item.turnIndex} accent="indigo" />
                    </Box>
                    <Box>
                        {sectionLabel('Classification')}
                        <KeyValueTable
                            entries={[
                                ['policy', selected.policyName],
                                ['verdict', selected.item.output],
                                ['confidence', formatPct(selected.item.confidence)],
                                ['person', selected.item.person],
                                ['department', selected.item.department],
                                ['when', formatWhen(selected.item.timestampIso)],
                            ]}
                            keyWidth="38%"
                        />
                    </Box>
                </Stack>
            )}
        </DetailDrawer>
    );
}
